import React, { useState, useEffect, useRef, useCallback, useMemo, FormEvent, ChangeEvent } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import io from 'socket.io-client';
import { useAuth } from '../contexts/AuthContext';
import ThemeBackground from '../components/ThemeBackground';
import VideoPlayer from '../components/VideoPlayer';
import VideoSourceSelector from '../components/VideoSourceSelector';
import ScrollToTop from '../components/ScrollToTop';
import config from '../config';
import UserProfileModal from '../components/UserProfileModal';
import { useRooms, safeIncrementViewers, safeDecrementViewers, safeUpsert } from '../hooks/useRooms';
import { supabase, handleVisibilityChange, diagnoseAndFixConnection } from '../lib/supabase';
import SystemIcon from '../components/SystemIcon';
import ReactPlayer from 'react-player'; // Importar ReactPlayer para la referencia

// Estilos para animaciones
const animationStyles = {
  '@keyframes slideIn': {
    from: {
      opacity: 0,
      transform: 'translateY(10px)'
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)'
    }
  },
  '@keyframes bounce': {
    '0%, 100%': {
      transform: 'translateY(0)'
    },
    '50%': {
      transform: 'translateY(-3px)'
    }
  }
};

interface Message {
  user: string;  // Email del usuario (para mantener compatibilidad)
  text: string;
  timestamp: number; // Timestamp en milisegundos
  username?: string; // Nombre de usuario del perfil
  avatar_url?: string; // URL de la imagen de perfil
  id?: string; // ID único para el mensaje
  readBy?: string[]; // Lista de IDs de usuarios que han leído el mensaje
}

// Utilidad para gestionar logs
const logger = {
  // Registro de logs ya mostrados para evitar duplicados
  shownLogs: new Set<string>(),

  // Función para mostrar logs solo en desarrollo
  log: (message: string, ...args: any[]) => {
    // Solo mostrar logs en desarrollo
    if (process.env.NODE_ENV !== 'development') return;

    // Si el mensaje es único o aún no se ha mostrado, mostrarlo
    if (!logger.shownLogs.has(message)) {
      console.log(message, ...args);
      // Añadir a la lista de mensajes mostrados para evitar duplicados excesivos
      logger.shownLogs.add(message);
    }
  },

  // Advertencias, siempre mostrar en desarrollo, limitar en producción
  warn: (message: string, ...args: any[]) => {
    // Siempre mostrar en desarrollo, limitar en producción
    if (process.env.NODE_ENV === 'development' || !logger.shownLogs.has(message)) {
      console.warn(message, ...args);
      logger.shownLogs.add(message);
    }
  },

  // Errores, siempre mostrar
  error: (message: string, ...args: any[]) => {
    console.error(message, ...args);
  },

  // Limpiar el caché de logs mostrados
  clearCache: () => {
    logger.shownLogs.clear();
  }
};

const Room = () => {
  const { id } = useParams<{ id: string }>();
  const roomId = id;
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();

  // Intentar cargar datos de sala de localStorage si existen
  const storedRoomData = roomId ? localStorage.getItem(`room_data_${roomId}`) : null;
  const initialRoomDataFromStorage = storedRoomData ? JSON.parse(storedRoomData) : null;

  // Logging para depuración (solo en desarrollo y solo una vez)
  const isLoggedRef = useRef(false);
  useEffect(() => {
    // Verificar si ya hemos hecho log y si estamos en desarrollo
    if (!isLoggedRef.current && process.env.NODE_ENV === 'development') {
      if (roomId) {
        console.log(`Room component inicializado con ID: ${roomId}`);
        console.log(`Datos de localStorage para sala ${roomId}:`, initialRoomDataFromStorage);
      } else {
        console.log('Room component inicializado SIN ID');
      }
      // Marcar que ya hicimos log
      isLoggedRef.current = true;
    }
  }, [roomId, initialRoomDataFromStorage]);

  const [url, setUrl] = useState('');
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [messages, setMessages] = useState<Message[]>([]);
  const [usersTyping, setUsersTyping] = useState<string[]>([]);
  const [isMicActive, setIsMicActive] = useState(false);
  const [isSpeakerActive, setIsSpeakerActive] = useState(true);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [volume, setVolume] = useState(1); // Volumen entre 0 y 1
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [roomData, setRoomData] = useState<{name: string, owner_id: string, is_public: boolean} | null>(
    initialRoomDataFromStorage || null
  );
  const [isOwner, setIsOwner] = useState(
    initialRoomDataFromStorage ? user?.id === initialRoomDataFromStorage.owner_id : false
  );
  const [isFullScreen, setIsFullScreen] = useState(false);
  const socketRef = useRef<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const seekTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSeekTimeRef = useRef<number>(0);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Ref para el timeout de typing
  const audioStreamRef = useRef<MediaStream | null>(null);
  const peerConnectionsRef = useRef<{[key: string]: RTCPeerConnection}>({});
  const volumeControlRef = useRef<HTMLDivElement>(null);
  const { updateRoomStatus } = useRooms();
  const [inputUrl, setInputUrl] = useState('');
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [loadedSeconds, setLoadedSeconds] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1); // Definir estado para playbackRate

  // Agregar estados y referencias faltantes
  const [videoUrl, setVideoUrl] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const lastProgressUpdateRef = useRef<number>(Date.now());
  const PROGRESS_UPDATE_INTERVAL = 1000; // Intervalo en ms para actualizar progreso
  const playerRef = useRef<ReactPlayer>(null); // Definir la referencia para el reproductor
  const [unreadMessages, setUnreadMessages] = useState(0); // Estado para mensajes no leídos
  const [isNearBottom, setIsNearBottom] = useState(true); // Estado para saber si el usuario está en la parte inferior
  const lastUnreadUpdateRef = useRef<number>(Date.now());
  const [readMessageIds, setReadMessageIds] = useState<{[key: string]: boolean}>({}); // Rastrear mensajes leídos
  const videoVisibleRef = useRef<boolean>(true); // Referencia para rastrear si el reproductor de video está visible

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      // Obtener el contenedor de mensajes (el elemento padre con overflow-y-auto)
      const chatContainer = messagesEndRef.current.parentElement;

      if (chatContainer) {
        // Verificar si el usuario ya está cerca del final antes de hacer scroll
        const isUserNearBottom = chatContainer.scrollHeight - chatContainer.clientHeight - chatContainer.scrollTop < 100;

        // Solo hacer scroll si el usuario está cerca del final o si es un mensaje propio
        if (isUserNearBottom) {
          // Usar scrollTop en lugar de scrollIntoView para evitar que desplace toda la página
          chatContainer.scrollTop = chatContainer.scrollHeight;
        }
      }
    }
  };

  // Cargar mensajes desde localStorage al iniciar
  useEffect(() => {
    if (!roomId) return;

    const storedMessages = localStorage.getItem(`chat_messages_${roomId}`);
    if (storedMessages) {
      try {
        const parsedMessages = (JSON.parse(storedMessages) as Message[]).map(msg => ({
          ...msg,
          id: msg.id || `${msg.timestamp}-${msg.user || 'unknown'}-${Math.random().toString(36).substr(2, 5)}`,
          readBy: Array.isArray(msg.readBy) ? msg.readBy : [] // Asegurar que readBy sea un array al cargar
        }));
        setMessages(parsedMessages);
      } catch (err) {
        console.error('Error al cargar mensajes guardados:', err);
      }
    }
  }, [roomId]);

  // Guardar mensajes en localStorage cuando cambian
  useEffect(() => {
    if (!roomId || messages.length === 0) return;

    localStorage.setItem(`chat_messages_${roomId}`, JSON.stringify(messages));
  }, [messages, roomId]);

  useEffect(() => {
    // Obtener el último mensaje
    const lastMessage = messages[messages.length - 1];

    if (!lastMessage) return;

    // Verificar la posición actual de scroll
    // Solo para mensajes propios hacemos scroll automático siempre
    if (lastMessage.user === user?.id) {
      // Usar un timeout para asegurar que el DOM se haya actualizado
      setTimeout(() => {
        if (messagesEndRef.current && messagesEndRef.current.parentElement) {
          messagesEndRef.current.parentElement.scrollTop = messagesEndRef.current.parentElement.scrollHeight;
        }
      }, 50);
    }
    // Para mensajes de otros o del sistema, verificamos primero la posición
    else {
      const isNearBottom = checkIfNearBottom();

      if (isNearBottom || lastMessage.user === 'system') {
        // Si está cerca del final o es mensaje del sistema, hacemos scroll
        setTimeout(() => {
          if (messagesEndRef.current && messagesEndRef.current.parentElement) {
            messagesEndRef.current.parentElement.scrollTop = messagesEndRef.current.parentElement.scrollHeight;
          }
        }, 50);
      } else {
        // Si no está cerca del final, incrementamos el contador
        setUnreadMessages(prev => {
          // Limitar el contador a un número razonable para evitar que se dispare
          const newCount = prev + 1;
          return newCount > 99 ? 99 : newCount; // Limitar a 99 como máximo
        });
      }
    }
  }, [messages, user?.id]);

  // Función para formatear la hora
  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // Si es 0, lo convertimos a 12
    return `${hours}:${minutes} ${ampm}`;
  };

  // Iniciar/detener el micrófono
  const toggleMicrophone = async () => {
    try {
      if (isMicActive) {
        // Detener micrófono
        if (audioStreamRef.current) {
          audioStreamRef.current.getTracks().forEach(track => track.stop());
          audioStreamRef.current = null;
        }
        setIsMicActive(false);
        socketRef.current.emit('voice-disconnected', roomId);

        console.log('Micrófono desactivado');
      } else {
        // Iniciar micrófono
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioStreamRef.current = stream;

        // Notificar al servidor que estamos conectados al canal de voz
        socketRef.current.emit('voice-connected', roomId);

        // Enviar el audio a todos los peers existentes
        Object.values(peerConnectionsRef.current).forEach(connection => {
          if (stream) {
            stream.getTracks().forEach(track => {
              connection.addTrack(track, stream);
            });
          }
        });

        setIsMicActive(true);
        console.log('Micrófono activado y emitiendo al canal de voz');
      }
    } catch (error) {
      console.error('Error al acceder al micrófono:', error);
      alert('No se pudo acceder al micrófono. Por favor, verifica los permisos de tu navegador.');
    }
  };

  // Activar/desactivar el altavoz
  const toggleSpeaker = () => {
    const newState = !isSpeakerActive;
    setIsSpeakerActive(newState);

    // Si activamos el altavoz, restauramos el volumen anterior
    // Si lo desactivamos, establecemos volumen a 0 pero guardamos el valor anterior
    const newVolume = newState ? (volume === 0 ? 1 : volume) : 0;
    setVolume(newVolume);

    // Silenciar todos los elementos de audio si es necesario
    const audioElements = document.querySelectorAll('audio');
    audioElements.forEach(audio => {
      audio.muted = !newState;
      audio.volume = newVolume;
    });

    console.log(`Altavoz ${newState ? 'activado' : 'silenciado'}`);
  };

  // Ajustar el volumen
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);

    // Si el volumen es 0, silenciamos el altavoz
    // Si el volumen es > 0 y el altavoz está silenciado, lo activamos
    if (newVolume === 0 && isSpeakerActive) {
      setIsSpeakerActive(false);
    } else if (newVolume > 0 && !isSpeakerActive) {
      setIsSpeakerActive(true);
    }

    // Aplicar el volumen a todos los elementos de audio
    const audioElements = document.querySelectorAll('audio');
    audioElements.forEach(audio => {
      audio.volume = newVolume;
      audio.muted = newVolume === 0;
    });
  };

  // Cerrar el control de volumen cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (volumeControlRef.current && !volumeControlRef.current.contains(event.target as Node)) {
        setShowVolumeSlider(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Función para crear una conexión entre peers
  const createPeerConnection = (peerId: string) => {
    try {
      console.log(`Creando conexión peer con ${peerId}`);

      const connection = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' },
          { urls: 'stun:stun3.l.google.com:19302' },
          { urls: 'stun:stun4.l.google.com:19302' }
        ]
      });

      // Agregar los tracks de audio local a la conexión
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => {
          connection.addTrack(track, audioStreamRef.current!);
        });
        console.log('Audio local agregado a la conexión peer');
      }

      // Configurar event handlers
      connection.onicecandidate = (event) => {
        if (event.candidate) {
          socketRef.current.emit('ice-candidate', {
            to: peerId,
            candidate: event.candidate
          });
          console.log('Enviando candidato ICE al peer');
        }
      };

      connection.ontrack = (event) => {
        console.log('Audio remoto recibido');
        // Crear un elemento de audio para reproducir el stream remoto
        const audio = document.createElement('audio');
        audio.srcObject = event.streams[0];
        audio.autoplay = true;
        audio.muted = !isSpeakerActive;
        document.body.appendChild(audio);

        // Añadir un identificador para poder limpiar
        audio.setAttribute('data-peer-id', peerId);
      };

      connection.onconnectionstatechange = () => {
        console.log(`Estado de conexión con ${peerId}: ${connection.connectionState}`);
      };

      peerConnectionsRef.current[peerId] = connection;
      return connection;
    } catch (error) {
      console.error('Error al crear la conexión peer:', error);
      return null;
    }
  };

  // Configurar la conexión WebRTC
  const setupVoiceConnection = (peerId: string, isInitiator: boolean) => {
    const peerConnection = createPeerConnection(peerId);
    if (!peerConnection) return;

    if (isInitiator) {
      peerConnection.createOffer()
        .then(offer => peerConnection.setLocalDescription(offer))
        .then(() => {
          socketRef.current.emit('voice-offer', {
            to: peerId,
            offer: peerConnection.localDescription
          });
        })
        .catch(error => console.error('Error al crear oferta:', error));
    }
  };

  // --- Funciones Definidas con useCallback ---

  const fetchRoomData = useCallback(async () => {
    if (!roomId) return;
    logger.log(`Buscando datos para la sala: ${roomId}`);
    try {
      const { data, error } = await supabase
        .from('rooms')
        .select('id, name, owner_id, is_public, video_url')
        .eq('id', roomId)
        .maybeSingle();

      if (error) {
        if (error.code === 'PGRST116') {
          logger.warn(`Sala ${roomId} no encontrada o inaccesible.`);
          setRoomData(null);
        } else {
          logger.error('Error al cargar datos de la sala:', error);
          setRoomData(null);
        }
        return false; // Indicar fallo
      }

      if (data) {
        logger.log('Datos de la sala cargados:', data);
        setRoomData(data);
        setIsOwner(user?.id === data.owner_id);
        setUrl(data.video_url || '');
        localStorage.setItem(`room_data_${roomId}`, JSON.stringify(data));
        return true; // Indicar éxito
      } else {
        logger.warn(`Sala ${roomId} no encontrada (maybeSingle devolvió null).`);
        setRoomData(null);
        return false; // Indicar fallo
      }
    } catch (err) {
      logger.error('Excepción al cargar datos de la sala:', err);
      setRoomData(null);
      return false; // Indicar fallo
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, user?.id, navigate]); // Dependencias: roomId, user.id (para isOwner), navigate

  const initializeSocket = useCallback(() => {
    if (socketRef.current) {
      logger.log("[Socket] Desconectando socket anterior antes de crear uno nuevo...");
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const serverUrl = config.wsUrl;
    logger.log(`[Socket] Intentando conectar a: ${serverUrl} con opciones:`, config.socketOptions);

    // Limpiar cualquier referencia de conexión anterior
    if (peerConnectionsRef.current) {
      Object.values(peerConnectionsRef.current).forEach(conn => conn.close());
      peerConnectionsRef.current = {};
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
      setIsMicActive(false);
    }

    try {
      // Verificar si estamos en un navegador que soporte WebSockets
      const isWebSocketSupported = 'WebSocket' in window;
      logger.log(`[Socket] ¿WebSocket soportado por el navegador?: ${isWebSocketSupported}`);

      // Clonar opciones para modificarlas
      const socketOptions = {
        ...config.socketOptions,
        query: {
          userId: user?.id,
          roomId: roomId
        }
      };

      // Si WebSocket no está soportado, forzar polling
      if (!isWebSocketSupported) {
        socketOptions.transports = ['polling'];
      }

      // Configurar socket sin intentar conectarse aún
      socketRef.current = io(serverUrl, socketOptions);

      logger.log("[Socket] Instancia de Socket creada. Configurando manejadores de eventos...");

      // Contador de intentos para los fallbacks locales
      let reconnectAttempts = 0;
      const MAX_LOCAL_RECONNECT_ATTEMPTS = 3;
      let modoComunidadActivo = false;

      // --- Manejadores de Eventos de Conexión ---
      socketRef.current.on('connect', () => {
        logger.log(`[Socket] Conectado exitosamente! ID: ${socketRef.current.id}, Transporte: ${socketRef.current.io.engine.transport.name}`);

        // Si estamos usando polling pero WebSocket está disponible, intentar actualizar a WebSocket
        if (socketRef.current.io.engine.transport.name === 'polling' && 'WebSocket' in window) {
          socketRef.current.io.engine.on('upgrade', (transport) => {
            logger.log(`[Socket] Transporte actualizado a: ${transport.name}`);
          });
        }

        if (modoComunidadActivo) {
          // Informar al usuario que ya estamos conectados
          setMessages(prev => [
            ...prev,
            {
              user: 'system',
              text: 'Conexión con el servidor restablecida. Todas las funciones están disponibles nuevamente.',
              timestamp: Date.now(),
              username: 'Sistema'
            }
          ]);
          modoComunidadActivo = false;
        }

        reconnectAttempts = 0; // Reiniciar contador

        socketRef.current.emit('join-room', {
          roomId,
          userId: user?.id,
          username: profile?.username || user?.email || 'Anónimo'
        });
      });

      socketRef.current.on('connect_error', (error: any) => {
        console.error(`[Socket] Error de conexión: ${error.message}`, error);
        console.log(`[Socket] Detalles de conexión: URL=${serverUrl}, Transporte=${socketRef.current?.io?.engine?.transport?.name || 'no disponible'}`);

        // Si estamos usando WebSocket y falla, intentar con polling
        if (socketRef.current &&
            socketRef.current.io &&
            socketRef.current.io.engine &&
            socketRef.current.io.engine.transport &&
            socketRef.current.io.engine.transport.name === 'websocket') {
          console.log('[Socket] WebSocket falló, intentando con polling explícitamente...');
          socketRef.current.io.opts.transports = ['polling'];
        }

        // Incrementar contador de intentos fallidos
        reconnectAttempts++;

        if (reconnectAttempts >= MAX_LOCAL_RECONNECT_ATTEMPTS && !modoComunidadActivo) {
          console.log('[Socket] Activando modo comunidad debido a errores de conectividad persistentes');
          modoComunidadActivo = true;

          // Mostrar mensaje al usuario solo una vez
          setMessages(prev => [
            ...prev,
            {
              user: 'system',
              text: 'Modo comunidad activado. Puedes ver el video pero la sincronización con otros usuarios está limitada.',
              timestamp: Date.now(),
              username: 'Sistema'
            }
          ]);
        }
      });

      socketRef.current.on('reconnect_attempt', (attemptNumber: number) => {
        console.log(`[Socket] Intento de reconexión #${attemptNumber}... Transporte: ${socketRef.current?.io?.engine?.transport?.name || 'desconocido'}`);

        // Alternar entre transportes en cada intento de reconexión
        if (attemptNumber > 2 && socketRef.current?.io?.opts?.transports) {
          // Si websocket falló, intentar con polling, y viceversa
          const currentTransport = socketRef.current.io.engine?.transport?.name;
          if (currentTransport === 'websocket') {
            console.log('[Socket] Cambiando a polling para este intento...');
            socketRef.current.io.opts.transports = ['polling'];
          } else if (currentTransport === 'polling' && 'WebSocket' in window) {
            console.log('[Socket] Cambiando a websocket para este intento...');
            socketRef.current.io.opts.transports = ['websocket'];
          }
        }
      });

      socketRef.current.on('reconnect_failed', () => {
        console.error("[Socket] Falló la reconexión después de múltiples intentos.");
        if (!modoComunidadActivo) {
          modoComunidadActivo = true;
          setMessages(prev => [
            ...prev,
            {
              user: 'system',
              text: 'No se pudo conectar al servidor. Puedes seguir viendo el video, pero la sincronización con otros usuarios no está disponible.',
              timestamp: Date.now(),
              username: 'Sistema'
            }
          ]);
        }
      });

      socketRef.current.on('error', (error: any) => {
        console.error("[Socket] Error general del socket:", error);
      });

      // --- Listeners de Eventos de Sala ---
      console.log("[Socket] Registrando listeners para eventos de la sala...");

      // Listener para cuando cambia el video (evento recibido del servidor)
      socketRef.current.on('video-change', (data: { url: string }) => {
        console.log(`[Socket] Recibido evento video-change del servidor: ${data.url}`);
        // Actualizar la URL del reproductor en este cliente
        setUrl(data.url);
        // Reiniciar estados relacionados con la carga
        setIsVideoLoading(true);
        setVideoReady(false);
        setProgress(0); // Reiniciar progreso
      });

      // Listener para play/pause/seek etc.
      socketRef.current.on('play', () => {
        console.log("[Socket] Recibido evento play del servidor");
        setPlaying(true);
      });

      socketRef.current.on('pause', () => {
        console.log("[Socket] Recibido evento pause del servidor");
        setPlaying(false);
      });

      socketRef.current.on('seek', (newProgress: number) => {
        console.log(`[Socket] Recibido evento seek del servidor: ${newProgress * 100}%`);
        // Solo actualizar si la diferencia es significativa para evitar bucles
        if (Math.abs(newProgress - progress) > 0.01) {
          setProgress(newProgress);
          // Si el reproductor existe, buscar directamente
          if (playerRef.current) {
            playerRef.current.seekTo(newProgress, 'fraction');
          }
        }
      });

      socketRef.current.on('playback-rate-change', (rate: number) => {
        console.log(`[Socket] Recibido evento playback-rate-change: ${rate}`);
        setPlaybackRate(rate);
        // Actualizar el reproductor si existe
        const player = playerRef.current?.getInternalPlayer();
        if (player && typeof player.setPlaybackRate === 'function') {
          player.setPlaybackRate(rate);
        }
      });

      socketRef.current.on('video-loading', () => {
        console.log('[Socket] Recibido evento video-loading del servidor');
        setIsVideoLoading(true);
        setVideoReady(false);
      });

      socketRef.current.on('video-ready', () => {
        console.log('[Socket] Recibido evento video-ready del servidor');
        setIsVideoLoading(false);
        setVideoReady(true);
        // Considerar si se debe iniciar la reproducción aquí
        // setPlaying(true);
      });

      // Listeners de chat y notificaciones
      socketRef.current.on('chat-message', (message: Message) => {
        console.log(`[Socket] Recibido chat-message de ${message.username}`);
        const messageWithIdAndReadBy = {
          ...message,
          id: message.id || `${message.timestamp}-${Math.random().toString(36).substr(2, 9)}`,
          readBy: Array.isArray(message.readBy) ? message.readBy : [] // Asegurar que readBy sea un array
        };
        requestAnimationFrame(() => {
          setMessages(prev => [...prev, messageWithIdAndReadBy]);
        });
      });

      socketRef.current.on('missed-messages', ({ messages: missedMessages }: { messages: Message[] }) => {
        console.log(`[Socket] Recibido missed-messages: ${missedMessages.length}`);
        // Lógica para combinar mensajes (si es necesario)
        setMessages(prev => {
          const existingTimestamps = new Set(prev.map(m => m.timestamp));
          const newMessages = missedMessages.filter(m => !existingTimestamps.has(m.timestamp));
          return [...prev, ...newMessages].sort((a, b) => a.timestamp - b.timestamp);
        });
      });

      socketRef.current.on('user-typing', (typingUser: string) => {
        setUsersTyping(prev => prev.includes(typingUser) ? prev : [...prev, typingUser]);
      });

      socketRef.current.on('user-stop-typing', (typingUser: string) => {
        setUsersTyping(prev => prev.filter(user => user !== typingUser));
      });

      socketRef.current.on('user-joined', ({ username }: { username: string }) => {
        console.log(`[Socket] Recibido user-joined: ${username}`);
        const systemMessage: Message = {
          user: 'system',
          text: `${username} se unió a la sala`,
          timestamp: Date.now(),
          username: 'Sistema'
        };
        setMessages(prev => [...prev, systemMessage]);
      });

      socketRef.current.on('new-message-notification', ({ username }: { username: string }) => {
        console.log(`[Socket] Recibido new-message-notification de ${username}`);
        if (isFullScreen) {
          const event = new CustomEvent('fullscreen-notification', { detail: { message: `Nuevo mensaje de ${username}` } });
          document.dispatchEvent(event);
        }
      });

      // Listeners de VOIP
      socketRef.current.on('user-joined-voice', (peerId: string) => { /* ... */ });
      socketRef.current.on('voice-offer', async ({ from, offer }: any) => { /* ... */ });
      socketRef.current.on('voice-answer', async ({ from, answer }: any) => { /* ... */ });
      socketRef.current.on('ice-candidate', async ({ from, candidate }: any) => { /* ... */ });
      socketRef.current.on('user-left-voice', (peerId: string) => { /* ... */ });

      // Añadir listener para confirmaciones de lectura recibidas del servidor
      socketRef.current.on('message-read-receipt', ({ messageId, readerUserId }) => {
        // Log eliminado - console.log(`[ReadReceipt] Recibido: msgId=${messageId}, reader=${readerUserId}`);
        setMessages(prevMessages => {
          let messageUpdated = false;
          const newMessages = prevMessages.map(msg => {
            const msgId = msg.id || msg.timestamp.toString();
            if (msgId === messageId) {
              const currentReadBy = Array.isArray(msg.readBy) ? msg.readBy : [];
              const updatedReadBy = currentReadBy.includes(readerUserId)
                                    ? currentReadBy
                                    : [...currentReadBy, readerUserId];

              // Log eliminado - console.log(`[ReadReceipt] Actualizando mensaje ${msgId}: readBy=${JSON.stringify(updatedReadBy)}`);
              messageUpdated = true;
              return { ...msg, readBy: updatedReadBy };
            }
            return msg;
          });

          // Log eliminado - if (!messageUpdated) { console.warn(...) }

          return newMessages;
        });
      });

      // Añadir listener para actualizaciones de lectura de mensajes (este ya estaba)
      socketRef.current.on('message-read-update', ({ messageId, userId }) => {
        if (userId === user?.id) return; // Ignorar nuestras propias actualizaciones

        setMessages(prev => prev.map(msg => {
          const msgId = msg.id || msg.timestamp.toString();
          if (msgId === messageId) {
            return {
              ...msg,
              readBy: [...(msg.readBy || []), userId]
            };
          }
          return msg;
        }));
      });

      // Intentar conectar manualmente después de configurar todos los listeners
      console.log("[Socket] Intentando conectar manually...");
      socketRef.current.connect();

    } catch (error) {
      console.error("[Socket] Error CRÍTICO al inicializar Socket.IO:", error);
      // Informar al usuario
      setMessages(prev => [
        ...prev,
        {
          user: 'system',
          text: 'Ocurrió un error al inicializar la conexión. El reproductor seguirá funcionando en modo local.',
          timestamp: Date.now(),
          username: 'Sistema'
        }
      ]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Dependencias que REALMENTE determinan la identidad de la conexión
    roomId,
    user?.id,
    profile?.username, // Incluir por si se usa en join-room
    // Las funciones setState y refs son estables y no causan re-render por sí mismas
    setUrl,
    setIsVideoLoading,
    setVideoReady,
    setProgress,
    setPlaying,
    setPlaybackRate,
    setMessages,
    setUsersTyping,
    playerRef,
    // Quitar dependencias que cambian frecuentemente y no afectan la conexión:
    // url, progress, isFullScreen
  ]); // Dependencias correctas y estables

  // Función para emitir eventos de forma segura con manejo de errores y fallbacks locales
  const safeSocketEmit = useCallback((event: string, data: any, callback?: Function) => {
    try {
      if (socketRef.current && socketRef.current.connected) {
        // Usar logger en lugar de console.log
        logger.log(`[Socket] Emitiendo "${event}"`, data);

        if (Array.isArray(data)) {
          socketRef.current.emit(event, ...data);
        } else {
          socketRef.current.emit(event, data);
        }
        if (callback) callback();
        return true;
      } else {
        // Usar logger.warn en lugar de console.warn
        logger.warn(`[Socket] No se pudo emitir "${event}" - Socket no conectado`);

        // Actualizar la interfaz localmente cuando sea posible
        if (event === 'play') {
          setPlaying(true);
        } else if (event === 'pause') {
          setPlaying(false);
        } else if (event === 'seek' && typeof data === 'number') {
          setProgress(Math.max(0, Math.min(1, data)));
        } else if (event === 'seek' && data?.time !== undefined) {
          setProgress(Math.max(0, Math.min(1, data.time)));
        } else if (event === 'video-change' && data?.url) {
          setUrl(data.url);
          setProgress(0);
        } else if (event === 'chat-message' && data) {
          // Para mensajes de chat, los agregamos directamente al estado local
          const newMessage = {
            ...data,
            timestamp: Date.now(),
            username: profile?.username || user?.email || 'Tú'
          };
          setMessages(prev => [...prev, newMessage]);
        }
        return false;
      }
    } catch (error) {
      logger.error(`[Socket] Error al emitir "${event}":`, error);
      return false;
    }
  }, [profile?.username, user?.email, setMessages]); // Añadir setMessages

  const unregisterSessionAndDecrement = useCallback(async () => {
    if (!roomId || !user?.id) {
        console.warn("[unregisterSessionAndDecrement] roomId o user.id inválidos");
        return;
    }
    console.log(`[unregisterSessionAndDecrement] Iniciando para sala ${roomId}`);
    try {
      // Eliminar la sesión específica
      const { error: deleteError } = await supabase
        .from('room_sessions')
        .delete()
        .match({ room_id: roomId, user_id: user.id });

      if (deleteError) {
         // Podría fallar si RLS lo impide o la sesión ya no existe, no necesariamente crítico
         console.warn("[unregisterSessionAndDecrement] Error al eliminar sesión (puede ser normal si ya salió):", deleteError.message);
      } else {
         console.log("[unregisterSessionAndDecrement] Sesión eliminada de DB.");
      }
    } catch (err) {
       console.error("[unregisterSessionAndDecrement] Excepción al eliminar sesión:", err);
    }

    // Llamar a safeDecrementViewers (que ya tiene su propia validación y logging)
    await safeDecrementViewers(roomId);
    console.log(`[unregisterSessionAndDecrement] safeDecrementViewers llamado para sala ${roomId}`);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, user?.id]); // Dependencias correctas

  const registerSession = useCallback(async () => {
    if (!roomId || !user?.id) {
        console.warn("[registerSession] roomId o user.id inválidos");
        return false;
    }
    console.log(`[registerSession] Iniciando para usuario ${user.id} en sala ${roomId}`);
    try {
      // Verificar si la sala existe
      const { data: roomInfo, error: roomError } = await supabase
          .from('rooms')
        .select('id', { count: 'exact', head: true }) // Solo necesitamos saber si existe
          .eq('id', roomId)
        .maybeSingle(); // Usar maybeSingle para evitar error si no existe

      if (roomError && roomError.code !== 'PGRST116') { // Ignorar error si solo es que no encontró filas
         console.error(`[registerSession] Error al verificar sala ${roomId}:`, roomError);
         return false;
      }

      if (!roomInfo && !roomError) { // roomError será null si maybeSingle devuelve null
         console.warn(`[registerSession] Sala ${roomId} no encontrada.`);
         // Considerar redirigir o mostrar error al usuario aquí
         // navigate('/not-found', { replace: true });
         return false;
      }

      console.log(`[registerSession] Sala ${roomId} existe. Procediendo a incrementar y registrar.`);

      // Incrementar viewers
      const incrementSuccess = await safeIncrementViewers(roomId);
      if (!incrementSuccess) {
         console.warn(`[registerSession] safeIncrementViewers falló para sala ${roomId}.`);
         // Decidir si continuar de todos modos o retornar false
      }

      // Registrar/Actualizar la sesión
      const sessionData = {
              room_id: roomId,
              user_id: user.id,
              last_active: new Date().toISOString(),
              is_active: true
      };
      const upsertSuccess = await safeUpsert('room_sessions', sessionData, 'room_id,user_id');

      if (upsertSuccess) {
         console.log('[registerSession] Sesión registrada/actualizada.');
         return true;
      } else {
         console.error('[registerSession] safeUpsert para room_sessions falló.');
         return false;
      }

    } catch (error) {
      console.error('[registerSession] Excepción:', error);
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, user?.id]); // Dependencias correctas

  // --- Efecto Principal ---
  const isInitializedRef = useRef(false);
  useEffect(() => {
    // Si ya estamos inicializados, no hacemos nada
    if (isInitializedRef.current) return;

    if (!roomId) {
      console.error("ID de sala no válido en Room, redirigiendo a inicio...");
      navigate('/', { replace: true });
      return;
    }

    // Solo loguear en modo desarrollo
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Effect] Montando Room ${roomId} para usuario ${user?.id}`);
    }

    let isMounted = true;
    let socketInitialized = false; // Flag para controlar inicialización del socket

    const initializeRoom = async () => {
      // Solo loguear en modo desarrollo
      if (process.env.NODE_ENV === 'development') {
        console.log("[Effect] Iniciando carga de datos...");
      }

      const roomExists = await fetchRoomData(); // Carga datos y devuelve true si existe

      if (isMounted && roomExists) {
        // Solo loguear en modo desarrollo
        if (process.env.NODE_ENV === 'development') {
          console.log("[Effect] Sala existe, inicializando socket y sesión...");
        }

        initializeSocket(); // Ahora inicializamos el socket
        socketInitialized = true;
        await registerSession(); // Registramos sesión

        // Solo loguear en modo desarrollo
        if (process.env.NODE_ENV === 'development') {
          console.log("[Effect] Socket y sesión inicializados.");
        }
      } else if (isMounted) {
         // Solo loguear en modo desarrollo
         if (process.env.NODE_ENV === 'development') {
           console.log("[Effect] La sala no existe o no se pudo cargar.");
         }
         // Aquí podríamos redirigir o mostrar un mensaje de error
         // navigate('/not-found');
      }

      // Marcar que ya hemos inicializado
      isInitializedRef.current = true;
    };

    initializeRoom();

    const handleAuthError = (event: Event) => {
      if (!isMounted) return;
      console.error("Error de autenticación global detectado en Room:", (event as CustomEvent).detail.message);
      // Considerar desloguear o redirigir
      // logout();
      // navigate('/login');
    };
    window.addEventListener('supabase-auth-error', handleAuthError);

    // Función de limpieza
    return () => {
      isMounted = false;

      // Solo loguear en modo desarrollo
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Cleanup] Desmontando Room ${roomId} para usuario ${user?.id}`);
      }

      if (socketInitialized && socketRef.current) {
        // Solo loguear en modo desarrollo
        if (process.env.NODE_ENV === 'development') {
          console.log("[Cleanup] Desconectando socket...");
        }
        // ... (código de desconexión del socket)
      }

      window.removeEventListener('supabase-auth-error', handleAuthError);

      // Solo loguear en modo desarrollo
      if (process.env.NODE_ENV === 'development') {
        console.log("[Cleanup] Llamando a unregisterSessionAndDecrement...");
      }
      unregisterSessionAndDecrement();
    };
  }, [roomId, user?.id, navigate, fetchRoomData, initializeSocket, registerSession, unregisterSessionAndDecrement]); // Añadir las funciones useCallback a las dependencias

  // Versiones seguras de los manejadores de eventos
  const handlePlayPause = () => {
    // Aplicar inmediatamente el cambio al estado local
    setPlaying(!playing);

    // Enviar el cambio al servidor para sincronizar con otros usuarios, usando la función segura
    safeSocketEmit(playing ? 'pause' : 'play', roomId);
    console.log(`Enviando acción ${playing ? 'pause' : 'play'} a otros usuarios`);
  };

  const handleSeek = (time: number) => {
    // Aplicar inmediatamente el cambio al estado local
    setProgress(Math.max(0, Math.min(1, time)));

    // Cancelar el timeout anterior si existe
    if (seekTimeoutRef.current) {
      clearTimeout(seekTimeoutRef.current);
    }

    // Enviar el cambio al servidor para sincronizar con otros usuarios
    // Usar un pequeño retraso para no saturar el servidor con eventos
    seekTimeoutRef.current = setTimeout(() => {
      console.log(`Enviando posición ${time * 100}% a otros usuarios`);
      safeSocketEmit('seek', { roomId, time });
    }, 100);
  };

  const handleProgress = useCallback(
    ({ played, loadedSeconds }: { played: number; loadedSeconds: number }) => {
      setLoadedSeconds(loadedSeconds);
      if (!videoReady && loadedSeconds > 5) {
        setVideoReady(true);
        setIsVideoLoading(false);
        safeSocketEmit('video-ready', null);
        safeSocketEmit('play', null);
      }

      if (videoReady && playing) {
        const now = Date.now();
        if (now - lastProgressUpdateRef.current > PROGRESS_UPDATE_INTERVAL) {
          lastProgressUpdateRef.current = now;
          setProgress(played);
        }
      }
    },
    [videoReady, playing, safeSocketEmit]
  );

  const handleVideoSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (videoUrl.trim() === "") return;

      safeSocketEmit('video-change', { url: videoUrl });
      setVideoUrl("");
      setIsVideoLoading(true);
      setVideoReady(false);
    },
    [videoUrl, safeSocketEmit]
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const text = e.target.value;
      setInputValue(text);

      // Limpiar timeout anterior si existe
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Si hay texto y no estábamos escribiendo antes, emitir 'typing'
      if (text && !isTyping) {
        setIsTyping(true);
        safeSocketEmit('typing', {
          roomId,
          user: profile?.username || user?.email || 'Anónimo', // <-- Cambiar a 'user'
        });
      }

      // Si hay texto, configurar un nuevo timeout para 'stop-typing'
      if (text) {
        typingTimeoutRef.current = setTimeout(() => {
          setIsTyping(false);
          safeSocketEmit('stop-typing', {
            roomId,
            user: profile?.username || user?.email || 'Anónimo', // <-- Cambiar a 'user'
          });
        }, 2000);
      }
      // Si no hay texto y estábamos escribiendo antes, emitir 'stop-typing' inmediatamente
      else if (!text && isTyping) {
        setIsTyping(false);
        safeSocketEmit('stop-typing', {
          roomId,
          user: profile?.username || user?.email || 'Anónimo', // <-- Cambiar a 'user'
        });
      }
    },
    // Añadir roomId a las dependencias
    [isTyping, setIsTyping, safeSocketEmit, profile?.username, user?.email, setInputValue, roomId]
  );

  const handleSendMessage = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (inputValue.trim() === "") return;

      const username = profile?.username || user?.email || 'Anónimo';
      // Crear el objeto mensaje completo, incluyendo avatar_url
      const message: Message = {
        user: user?.id || 'anonymous',
        text: inputValue,
        timestamp: Date.now(),
        username,
        avatar_url: profile?.avatar_url || '',
        id: `${Date.now()}-${user?.id || 'anon'}-${Math.random().toString(36).substr(2, 5)}`,
        readBy: user?.id ? [user.id] : [] // Inicializar con el ID del emisor
      };

      // 1. Añadir el mensaje al estado local INMEDIATAMENTE
      setMessages(prev => [...prev, message]);

      // 2. Enviar el mensaje al servidor
      safeSocketEmit('chat-message', { roomId, message });

      // 3. Emitir 'stop-typing' inmediatamente al enviar
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      if (isTyping) {
        safeSocketEmit('stop-typing', {
          roomId,
          user: profile?.username || user?.email || 'Anónimo', // <-- Cambiar a 'user'
        });
      }

      // Notificar a usuarios en pantalla completa
      if (isFullScreen) {
        safeSocketEmit('new-message-notification', { username });
      }

      // Limpiar input y estado de "escribiendo"
      setInputValue("");
      setIsTyping(false);

      // Asegurar el scroll al final para mensajes propios
      setTimeout(() => {
        if (messagesEndRef.current && messagesEndRef.current.parentElement) {
          messagesEndRef.current.parentElement.scrollTop = messagesEndRef.current.parentElement.scrollHeight;
          // También actualizamos el estado de posición y mensajes no leídos
          setIsNearBottom(true);
          setUnreadMessages(0);
        }
      }, 50);
    },
    // Añadir isTyping a las dependencias
    [inputValue, profile?.username, profile?.avatar_url, user?.id, user?.email, isFullScreen, safeSocketEmit, roomId, setMessages, isTyping]
  );

  const handleVideoSelect = useCallback(
    (videoUrl: string) => {
      // Actualizar la URL del reproductor localmente
      setUrl(videoUrl);

      // Limpiar el estado del input si es diferente (asumiendo que hay un estado para el input)
      if (inputValue === videoUrl) {
        setInputValue("");
      }

      // Emitir el cambio al servidor
      safeSocketEmit('video-change', { url: videoUrl });

      // Notificar a otros usuarios quién cambió el video
      const username = profile?.username || user?.email || 'Anónimo';
      const systemMessage = {
        user: 'system',
        text: `${username} cambió el video`,
        timestamp: Date.now(),
        username: 'Sistema'
      };

      // Emitir eventos de carga y mensaje de chat
      safeSocketEmit('video-loading', null);
      safeSocketEmit('chat-message', systemMessage);

      // Actualizar estado local relacionado con la carga del video
      setIsVideoLoading(true);
      setVideoReady(false);
    },
    [profile?.username, user?.email, safeSocketEmit, setUrl, setInputValue, inputValue] // Añadir dependencias
  );

  const handlePlaybackRateChange = useCallback((rate: number) => {
    const player = document.querySelector('video');
    if (player) {
      player.playbackRate = rate;
      safeSocketEmit('playback-rate-change', rate);
    }
  }, [safeSocketEmit]);

  const handleLeaveRoom = () => {
    // 1. Desconectar el socket inmediatamente
    if (socketRef.current) {
      console.log("[Leave] Desconectando socket...");
      socketRef.current.disconnect();
      socketRef.current = null; // Limpiar referencia
    }

    // 2. Realizar limpieza de sesión y decremento de viewers
    if (roomId && user?.id) {
      console.log('[Leave] Iniciando limpieza de sesión y decremento...');
      // Llamar a la función que ya encapsula la lógica de Supabase y RPC
      unregisterSessionAndDecrement().then(() => {
        console.log("[Leave] unregisterSessionAndDecrement completado.");
        // 3. Ejecutar limpieza de salas inactivas como última acción
        supabase.rpc('cleanup_sessions_public')
          .then(() => console.log('[Leave] Limpieza de sesiones ejecutada tras salir.'),
                err => console.error('[Leave] Error en limpieza de sesiones tras salir:', err));
      }).catch(err => {
        console.error("[Leave] Error durante unregisterSessionAndDecrement:", err);
      });

      // 4. Limpiar cualquier estado local persistente relacionado con esta sala
        localStorage.removeItem(`session-${user.id}-${roomId}`);
      localStorage.removeItem(`room_data_${roomId}`);
      // Considerar limpiar chat_messages_${roomId} si no se quiere persistencia entre visitas
      // localStorage.removeItem(`chat_messages_${roomId}`);

      // 5. Eliminar la llamada fetch a /api/room-exit (Endpoint no necesario/implementado)
      // fetch(`${config.apiUrl}/api/room-exit`, { ... }).catch(...);
      console.log("[Leave] Llamada fetch a /api/room-exit OMITIDA.");

    } else {
      console.warn("[Leave] No se pudo realizar limpieza completa (faltan roomId o user.id)");
    }

    // 6. Navegar fuera de la sala
    console.log("[Leave] Navegando a la página principal...");
    navigate('/');
  };

  // Actualizar contador de espectadores - versión robusta
  useEffect(() => {
    if (!roomId || !user?.id) return;

    let viewerIncremented = false;
    const sessionKey = `session-${user.id}-${roomId}`;

    // Verificar si ya tenemos una sesión activa para este usuario en esta sala
    const existingSession = localStorage.getItem(sessionKey);

    // Función para incrementar el contador de viewers una sola vez
    const incrementViewers = async () => {
      try {
        // Si ya incrementamos en esta sesión, no hacerlo de nuevo
        if (viewerIncremented || existingSession) {
          console.log('No se incrementa viewers, ya está incrementado');
          return;
        }

        console.log('Incrementando contador de espectadores...');

        // Marcar como incrementado antes de la operación para evitar múltiples llamadas
        viewerIncremented = true;
        localStorage.setItem(sessionKey, 'true');

        // Incrementar en la base de datos usando el método robusto
        const success = await safeIncrementViewers(roomId);

        if (success) {
        console.log('Espectador añadido a la sala:', roomId);
        } else {
          console.warn('No se pudo incrementar el contador, pero continuando...');
        }
      } catch (err) {
        console.error('Error al incrementar espectadores:', err);
        // No marcar como fallido para permitir reintentos
        viewerIncremented = false;
        localStorage.removeItem(sessionKey);
      }
    };

    // Ejecutar solo al montar el componente y si no existe sesión previa
    if (!existingSession) {
      incrementViewers();
    } else {
      console.log('Sesión existente detectada, no se incrementa el contador');
      viewerIncremented = true;
    }

    // Retornar función de limpieza
    return () => {
      // Decrementar el contador solo si lo incrementamos en esta sesión
      if (viewerIncremented) {
        console.log('Decrementando contador de espectadores al desmontar');

        // Limpiar marca de sesión
        localStorage.removeItem(sessionKey);

        // Decrementar en la base de datos usando el método robusto
        safeDecrementViewers(roomId)
          .then(success => {
            if (success) {
              console.log('Decrementado contador de viewers al desmontar componente');
            } else {
              console.warn('No se pudo decrementar el contador al desmontar, pero continuando...');
            }
          });
      }
    };
  }, [roomId, user?.id]); // Solo las dependencias esenciales

  // Añadir un nuevo efecto para ayudar a limpiar sesiones inactivas
  useEffect(() => {
    if (!roomId || !user?.id) return;

    // Función para invocar la limpieza de sesiones
    const triggerCleanup = async () => {
      try {
        console.log('Solicitando limpieza de sesiones inactivas...');
        await supabase.rpc('cleanup_sessions_public');
        console.log('Limpieza de sesiones completada');
      } catch (err) {
        console.error('Error al solicitar limpieza de sesiones:', err);
      }
    };

    // Ejecutar limpieza al montar el componente
    triggerCleanup();

    // Configurar un intervalo para ejecutar la limpieza cada 30 segundos
    const cleanupInterval = setInterval(triggerCleanup, 30000);

    return () => {
      clearInterval(cleanupInterval);
    };
  }, [roomId, user?.id]);

  // Verificar señales de desconexión
  useEffect(() => {
    if (!roomId) return;

    const checkExitSignals = () => {
      try {
        // Buscar todas las claves de almacenamiento para encontrar señales de salida
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith(`room-exit-${roomId}-`)) {
            const userId = key.split('-')[3];
            const timestamp = parseInt(localStorage.getItem(key) || '0');

            // Si la señal es reciente (menos de 1 minuto)
            if (Date.now() - timestamp < 60000) {
              console.log(`Detectada señal de salida para usuario ${userId}`);

              // Decrementar el contador para este usuario de forma robusta
              safeDecrementViewers(roomId)
                .then(success => {
                  if (success) {
                    console.log(`Contador decrementado para usuario ${userId} por señal de salida`);
                    // Eliminar la señal después de procesarla
                    localStorage.removeItem(key);
                  } else {
                    console.warn(`No se pudo decrementar contador para señal de salida de ${userId}, pero continuando...`);
                  }
                });
            } else {
              // Si la señal es antigua, eliminarla
              localStorage.removeItem(key);
            }
          }
        }
      } catch (err) {
        console.error('Error al verificar señales de salida:', err);
      }
    };

    // Verificar señales cada 10 segundos
    const signalInterval = setInterval(checkExitSignals, 10000);

    // Verificación inicial
    checkExitSignals();

    return () => {
      clearInterval(signalInterval);
    };
  }, [roomId]);

  // Actualizar URL del video en la base de datos
  useEffect(() => {
    if (!roomId || !url || !isOwner) return;

    const updateVideoUrl = async () => {
      try {
        await supabase
          .from('rooms')
          .update({ video_url: url, last_active: new Date().toISOString() })
          .eq('id', roomId);
      } catch (err) {
        console.error('Error al actualizar URL del video:', err);
      }
    };

    // Usar un timeout para no actualizar en cada cambio pequeño
    const timer = setTimeout(() => {
      updateVideoUrl();
    }, 2000);

    return () => clearTimeout(timer);
  }, [roomId, url, isOwner]);

  // Actualizar último tiempo de actividad periódicamente
  useEffect(() => {
    if (!roomId || !user?.id) return;

    const sessionKey = `session-${user.id}-${roomId}`;

    // Registrar la sesión activa al montar
    localStorage.setItem(sessionKey, 'true');

    console.log(`Registrando sesión activa: ${sessionKey}`);

    // Actualizar la sala periódicamente para indicar actividad
    const updateInterval = setInterval(() => {
      // Si el documento está visible, actualizar el estado de actividad
      if (!document.hidden) {
        try {
          // Actualizar último tiempo de actividad en la sala
          supabase
            .from('rooms')
            .update({ last_active: new Date().toISOString() })
            .eq('id', roomId)
            .then(() => {
              console.log('Sala marcada como activa:', roomId);
            });

          // Mantener el localStorage actualizado también
          localStorage.setItem(sessionKey, 'true');
        } catch (error) {
          console.error('Error al actualizar último tiempo de actividad:', error);
        }
      }
    }, 30000); // Cada 30 segundos

    // Limpiar intervalo al desmontar
    return () => {
      clearInterval(updateInterval);

      // Eliminar la sesión al salir
      localStorage.removeItem(sessionKey);
      console.log(`Sesión terminada: ${sessionKey}`);
    };
  }, [roomId, user?.id]);

  // Función para cambiar la privacidad de la sala
  const toggleRoomPublic = async () => {
    if (!roomData || !isOwner) return;

    try {
      const newStatus = !roomData.is_public;

      // Actualizar en Supabase mediante el hook
      await updateRoomStatus(roomId!, newStatus);

      // Actualizar estado local
      setRoomData({
        ...roomData,
        is_public: newStatus
      });
    } catch (err) {
      console.error('Error al cambiar privacidad de la sala:', err);
    }
  };

  const loadVideo = () => {
    if (inputUrl.trim()) {
      setUrl(inputUrl);
      setProgress(0);
      socketRef.current.emit('video-change', { roomId, url: inputUrl });
    }
  };

  // Añadir también un timeout de seguridad para videos que tardan demasiado
  useEffect(() => {
    if (isVideoLoading && !videoReady && url) {
      // Si después de 10 segundos el video sigue cargando, considerarlo como listo de todas formas
      const safetyTimeout = setTimeout(() => {
        console.log("Timeout de seguridad activado - forzando estado de video listo");
        setVideoReady(true);
        setIsVideoLoading(false);
        setPlaying(true);
        socketRef.current.emit('video-ready', { roomId, url });
        socketRef.current.emit('play', roomId);
      }, 10000);

      return () => clearTimeout(safetyTimeout);
    }
  }, [isVideoLoading, videoReady, url, roomId]);

  // Dentro del useEffect que maneja los eventos de notificación y visibilidad del reproductor
  useEffect(() => {
    // Función para manejar cambios de visibilidad en la sala
    const handleRoomVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('Sala: Página visible de nuevo, ejecutando proceso de reconexión centralizado...');

        try {
          // --- PASO 1: Diagnosticar y solucionar problemas de conexión ---
          console.log("Diagnosticando problemas de conexión para la sala...");
          const diagnosis = await diagnoseAndFixConnection();
          console.log("Resultado del diagnóstico para la sala:", diagnosis);

          // Si el cliente está en mal estado después del diagnóstico, recargar la página
          if (diagnosis.clientStatus === 'error') {
            console.log("Cliente en mal estado para la sala, recargando página...");
            window.location.reload();
            return;
          }

          // --- PASO 2: Ejecutar el proceso de reconexión centralizado ---
          const reconnected = await handleVisibilityChange();
          if (!reconnected) {
            console.error('Error en el proceso de reconexión centralizado para la sala');
            // Intentar continuar de todos modos
          } else {
            console.log('Reconexión completada exitosamente para la sala');
          }

          // --- PASO 3: Reconectar el socket si es necesario ---
          if (socketRef.current) {
            if (socketRef.current.disconnected) {
              console.log('Reconectando socket...');
              socketRef.current.connect();
            } else {
              console.log('Socket ya conectado, verificando estado...');
              // Verificar si el socket está realmente conectado enviando un ping
              socketRef.current.emit('ping', { roomId }, (response: any) => {
                if (!response || response.error) {
                  console.log('Socket no responde correctamente, reconectando...');
                  socketRef.current?.disconnect();
                  setTimeout(() => {
                    socketRef.current?.connect();
                  }, 500);
                } else {
                  console.log('Socket responde correctamente');
                }
              });
            }
          }

          // --- PASO 4: Verificar si la reconexión fue exitosa ---
          const finalCheck = await diagnoseAndFixConnection();
          if (finalCheck.clientStatus === 'error' || finalCheck.sessionStatus === 'error') {
            console.log("La reconexión no fue completamente exitosa para la sala, recargando página...");
            window.location.reload();
            return;
          }
        } catch (error) {
          console.error('Error durante el proceso de reconexión de la sala:', error);
          // SOLUCIÓN RADICAL: Recargar la página si hay un error grave
          console.log("Error grave durante la reconexión de la sala, recargando página...");
          window.location.reload();
        }
      }
    };

    // Agregar listener para cambios de visibilidad
    document.addEventListener('visibilitychange', handleRoomVisibilityChange);

    // Manejar notificaciones del reproductor de video
    const handleVideoNotification = (event: any) => {
      // Crear un mensaje de sistema para la notificación
      const systemMessage: Message = {
        user: 'system',
        text: event.detail.message,
        timestamp: Date.now(),
        username: 'Sistema'
      };

      // Usar requestAnimationFrame para evitar operación costosa durante scroll o animación
      requestAnimationFrame(() => {
        // Agregar al chat como mensaje del sistema
        setMessages(prev => [...prev, systemMessage]);
      });
    };

    // Manejar cambios del estado de pantalla completa
    const handleFullscreenChange = () => {
      // Usar requestAnimationFrame para evitar repintados múltiples
      requestAnimationFrame(() => {
        setIsFullScreen(!!document.fullscreenElement);
      });
    };

    // Manejar notificaciones que deben aparecer en pantalla completa
    const handleFullscreenNotification = (event: any) => {
      if (isFullScreen && socketRef.current) {
        // Emitir un evento al reproductor del video
        const notificationEvent = new CustomEvent('video-notification', {
          detail: { message: event.detail.message }
        });

        // Usar setTimeout con 0ms para diferir la operación al final del ciclo de eventos actual
        setTimeout(() => {
          document.dispatchEvent(notificationEvent);
        }, 0);
      }
    };

    // Función para manejar el scroll en dispositivos móviles
    // Esto ayuda a evitar problemas cuando el usuario baja al chat y vuelve a subir
    const handleScroll = () => {
      // Detectar si estamos en un dispositivo móvil
      const isMobile = window.innerWidth < 640 || 'ontouchstart' in window;
      if (!isMobile) return;

      // Obtener el elemento del reproductor de video
      const videoContainer = document.querySelector('.react-player');
      if (!videoContainer) return;

      // Verificar si el reproductor está visible en la pantalla
      const rect = videoContainer.getBoundingClientRect();
      const isVisible = (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );

      // Si el reproductor acaba de hacerse visible después de estar oculto
      // (por ejemplo, cuando el usuario vuelve a subir desde el chat)
      if (isVisible && !videoVisibleRef.current) {
        console.log('Reproductor de video visible nuevamente, verificando estado...');
        videoVisibleRef.current = true;

        // Verificar si el reproductor está en un estado válido
        if (playerRef.current) {
          try {
            // Intentar acceder al reproductor interno
            const internalPlayer = playerRef.current.getInternalPlayer();

            if (!internalPlayer) {
              console.log('Reproductor interno no disponible, reiniciando...');
              // Forzar una recarga del reproductor
              const currentUrl = url;
              setUrl('');

              // Usar requestAnimationFrame para asegurar que la UI se actualice correctamente
              requestAnimationFrame(() => {
                setTimeout(() => {
                  setUrl(currentUrl);
                }, 100);
              });
            } else {
              console.log('Reproductor interno disponible, continuando reproducción normal');
              // Si el reproductor está disponible y debería estar reproduciendo, asegurarse de que lo esté
              if (playing && typeof internalPlayer.playVideo === 'function') {
                internalPlayer.playVideo();
              }
            }
          } catch (error) {
            console.error('Error al verificar estado del reproductor:', error);
          }
        }
      } else if (!isVisible && videoVisibleRef.current) {
        // El reproductor acaba de ocultarse
        videoVisibleRef.current = false;
      }
    };

    // Usar la referencia definida en el nivel superior

    // Escuchar eventos de notificación desde el reproductor con passive cuando sea posible
    document.addEventListener('video-notification', handleVideoNotification, { passive: true });
    document.addEventListener('fullscreenchange', handleFullscreenChange, { passive: true });
    document.addEventListener('fullscreen-notification', handleFullscreenNotification, { passive: true });

    // Agregar listener para el evento de scroll
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Limpiar listeners al desmontar
    return () => {
      document.removeEventListener('visibilitychange', handleRoomVisibilityChange);
      document.removeEventListener('video-notification', handleVideoNotification);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('fullscreen-notification', handleFullscreenNotification);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isFullScreen, url, playing]); // Agregar isFullScreen, url y playing como dependencias

  // Actualizar la URL del video en la base de datos de forma robusta
  const updateVideoUrlInDatabase = async (videoUrl: string) => {
    if (!roomId || !isOwner) return;

    try {
      console.log(`Guardando URL de video en la base de datos: ${videoUrl}`);

      const success = await safeUpsert('rooms', {
        id: roomId,
        video_url: videoUrl,
          last_active: new Date().toISOString()
      }, 'id');

      if (success) {
        console.log('URL del video actualizada correctamente en la base de datos');
      } else {
        console.warn('No se pudo actualizar la URL del video, pero continuando...');
      }
    } catch (err) {
      console.error('Error al actualizar URL del video:', err);
    }
  };

  // Añadir un nuevo efecto para gestionar el mantenimiento de sesión
  useEffect(() => {
    if (!roomId || !user?.id) return;

    // Función para asegurar que la sesión se mantiene activa
    const keepSessionAlive = async () => {
      try {
        // Solo loguear en modo desarrollo
        if (process.env.NODE_ENV === 'development') {
          console.log('Manteniendo sesión activa para la sala:', roomId);
        }

        // Actualizar el registro de sesión en la base de datos
        await supabase
          .from('room_sessions')
          .upsert({
            room_id: roomId,
            user_id: user.id,
            is_active: true,
            last_active: new Date().toISOString()
          }, {
            onConflict: 'room_id,user_id'
          });

        // También actualizar la sala para mostrar actividad, obteniendo los datos actuales primero
        const { data: currentRoomData } = await supabase
          .from('rooms')
          .select('viewers')
          .eq('id', roomId)
          .single();

        await supabase
          .from('rooms')
          .update({
            last_active: new Date().toISOString(),
            viewers: Math.max(1, currentRoomData?.viewers || 1) // Usar los datos obtenidos directamente
          })
          .eq('id', roomId);

        // Solo loguear en modo desarrollo
        if (process.env.NODE_ENV === 'development') {
          console.log('Sesión y sala actualizadas correctamente');
        }
      } catch (err) {
        console.error('Error al mantener la sesión activa:', err);
      }
    };

    // Ejecutar inmediatamente para asegurar que la sesión esté activa
    keepSessionAlive();

    // Configurar un intervalo para mantener la sesión activa cada 30 segundos
    const keepAliveInterval = setInterval(keepSessionAlive, 30000);

    // Limpiar el intervalo al desmontar
    return () => {
      clearInterval(keepAliveInterval);
    };
  }, [roomId, user?.id]); // Quitar roomData de las dependencias

  // Efecto para mantener activa la sesión (heartbeat)
  useEffect(() => {
    if (!roomId || !user?.id) return;

    // Función para actualizar la actividad
    const updateActivity = async () => {
      try {
        // Registrar actividad en la base de datos usando método robusto
        const success = await safeUpsert('room_sessions', {
            room_id: roomId,
            user_id: user.id,
            last_active: new Date().toISOString(),
            is_active: true
        }, 'room_id,user_id');

        // Solo loguear en modo desarrollo
        if (process.env.NODE_ENV === 'development') {
          if (success) {
            console.log('Heartbeat: actividad registrada');
          } else {
            console.warn('No se pudo registrar heartbeat, pero continuando...');
          }
        }
      } catch (err) {
        console.error('Error al registrar actividad de heartbeat:', err);
      }
    };

    // Registrar actividad inicial
    updateActivity();

    // Configurar intervalo de heartbeat
    const heartbeatInterval = setInterval(updateActivity, 60000); // Cada minuto

    // Limpiar intervalo al desmontar
    return () => {
      clearInterval(heartbeatInterval);
    };
  }, [roomId, user?.id]);

  // Agregar un efecto para capturar el cierre de la pestaña
  useEffect(() => {
    if (!roomId || !user?.id) return;

    // Función para manejar el cierre de la pestaña
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Solo loguear en modo desarrollo
      if (process.env.NODE_ENV === 'development') {
        console.log('Usuario cerrando pestaña, limpiando sesión...');
      }

      // Mensaje para confirmar la salida (aunque muchos navegadores modernos lo ignoran)
      event.preventDefault();

      // Limpiar localStorage
      localStorage.removeItem(`session-${user.id}-${roomId}`);

      // Establece una señal de salida
      localStorage.setItem(`room-exit-${roomId}-${user.id}`, Date.now().toString());

      // Intento síncrono de marcar la sesión como inactiva (puede no completarse)
      try {
        const exitBeacon = navigator.sendBeacon(
          `${config.apiUrl}/api/room-exit`,
          new Blob([JSON.stringify({
            roomId,
            userId: user.id,
            action: 'exit'
          })], { type: 'application/json' })
        );

        // Solo loguear en modo desarrollo
        if (process.env.NODE_ENV === 'development') {
          console.log('Señal de exit enviada:', exitBeacon);
        }
      } catch (error) {
        console.error('Error al enviar beacon:', error);
      }

      // El mensaje de confirmación que puede mostrarse (dependiendo del navegador)
      return '¿Estás seguro de que quieres salir? Si sales, se te eliminará de la sala.';
    };

    // Agregar event listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Limpiar al desmontar
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [roomId, user?.id]);

  // Función para verificar si el usuario está cerca del final del chat
  const checkIfNearBottom = useCallback(() => {
    if (messagesEndRef.current && messagesEndRef.current.parentElement) {
      const chatContainer = messagesEndRef.current.parentElement;
      const isUserNearBottom = chatContainer.scrollHeight - chatContainer.clientHeight - chatContainer.scrollTop < 100;
      setIsNearBottom(isUserNearBottom);
      return isUserNearBottom;
    }
    return false;
  }, []);

  // Marcar mensaje como leído
  const markMessageAsRead = useCallback((messageId: string) => {
    if (!user?.id || !messageId) return;

    setReadMessageIds(prev => {
      // Solo actualizar si no está ya marcado como leído
      if (prev[messageId]) return prev;

      // Crear una copia y añadir el nuevo mensaje leído
      return {
        ...prev,
        [messageId]: true
      };
    });

    // También enviar al servidor que el mensaje ha sido leído
    if (socketRef.current?.connected) {
      safeSocketEmit('message-read', {
        messageId,
        userId: user.id,
        roomId
      });
    }
  }, [user?.id, roomId, safeSocketEmit]); // Asegurar dependencias correctas

  // Marcar los mensajes visibles como leídos cuando el usuario está cerca del final
  const checkAndMarkVisibleMessagesAsRead = useCallback(() => {
    if (!messagesEndRef.current?.parentElement || !isNearBottom || !user?.id) return;

    // Obtener los últimos mensajes que no son del usuario actual y no han sido leídos
    const unreadMessages = messages
      .filter(m => m.user !== user.id && m.user !== 'system' && !readMessageIds[m.id || m.timestamp.toString()]);

    // Marcar los últimos 5 mensajes como leídos (suponiendo que son los visibles)
    const visibleUnreadMessages = unreadMessages.slice(-5);

    visibleUnreadMessages.forEach(message => {
      const messageId = message.id || message.timestamp.toString();
      markMessageAsRead(messageId);
    });
  }, [messages, user?.id, isNearBottom, readMessageIds, markMessageAsRead]);

  // Función para marcar todos los mensajes no leídos como leídos
  const markAllMessagesAsRead = useCallback(() => {
    if (!user?.id || !messages.length) return;

    // Filtrar mensajes no propios y no del sistema que no estén marcados como leídos
    const unreadMessages = messages.filter(message =>
      message.user !== user.id &&
      message.user !== 'system' &&
      !readMessageIds[message.id || message.timestamp.toString()]
    );

    // Marcar todos esos mensajes como leídos
    unreadMessages.forEach(message => {
      const messageId = message.id || message.timestamp.toString();
      markMessageAsRead(messageId);
    });
  }, [messages, user?.id, readMessageIds, markMessageAsRead]);

  // Función para manejar el foco en el input y marcar los mensajes como leídos
  const handleInputFocus = useCallback(() => {
    // Marcar todos los mensajes como leídos cuando el usuario hace foco en el input
    markAllMessagesAsRead();
    // También resetear el contador de mensajes no leídos
    setUnreadMessages(0);
  }, [markAllMessagesAsRead]);

  // Función para manejar el scroll del chat
  const handleChatScroll = useCallback(() => {
    const isNearBottom = checkIfNearBottom();

    if (isNearBottom) {
      // Si está cerca del final y hay mensajes no leídos, resetear el contador
      if (unreadMessages > 0) {
        setUnreadMessages(0);
      }

      // Si está cerca del final, verificar mensajes para marcarlos como leídos
      requestAnimationFrame(() => {
        checkAndMarkVisibleMessagesAsRead();
      });
    }
  }, [unreadMessages, checkIfNearBottom, checkAndMarkVisibleMessagesAsRead]);

  return (
    <div className="relative min-h-screen overflow-y-auto custom-scrollbar">
      <style>
        {`
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .animate-slide-in {
            animation: slideIn 0.3s ease-out forwards;
          }

          @keyframes bounce {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-3px);
            }
          }

          .animate-dot-bounce {
            animation: bounce 0.6s infinite;
          }

          @keyframes bounceIn {
            0% {
              opacity: 0;
              transform: scale(0.3);
            }
            50% {
              opacity: 1;
              transform: scale(1.05);
            }
            70% { transform: scale(0.9); }
            100% { transform: scale(1); }
          }

          .animate-bounce-in {
            animation: bounceIn 0.5s ease-out;
          }

          .delay-0 {
            animation-delay: 0s;
          }

          .delay-75 {
            animation-delay: 0.1s;
          }

          .delay-150 {
            animation-delay: 0.2s;
          }

          /* Estilos para scrollbar personalizado */
          .custom-scrollbar::-webkit-scrollbar {
            width: 8px;
          }

          .custom-scrollbar::-webkit-scrollbar-track {
            background: rgba(15, 23, 42, 0.3);
            border-radius: 10px;
          }

          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(59, 130, 246, 0.5);
            border-radius: 10px;
          }

          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(59, 130, 246, 0.8);
          }

          /* Para Firefox */
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: rgba(59, 130, 246, 0.5) rgba(15, 23, 42, 0.3);
          }

          /* Para dispositivos táctiles */
          @media (pointer: coarse) {
            .custom-scrollbar::-webkit-scrollbar {
              width: 14px; /* Scrollbar más ancho para dispositivos táctiles */
            }
          }

          /* Estilos para móviles */
          @media (max-width: 640px) {
            .mobile-padding {
              padding-bottom: 80px !important;
              padding-left: 16px !important;
              padding-right: 16px !important;
            }

            /* Asegurar compatibilidad con iOS Safari */
            html, body, #__next, .min-h-screen {
              height: -webkit-fill-available;
            }

            /* Corregir problema del reproductor en móviles */
            .react-player {
              position: relative !important;
              overflow: hidden !important;
              width: 100% !important;
              height: auto !important;
              min-height: 180px !important;
            }

            .react-player iframe {
              position: absolute !important;
              top: 0 !important;
              left: 0 !important;
              width: 100% !important;
              height: 100% !important;
            }

            /* Mejoras para el mensaje de video no disponible en móviles */
            .w-16.h-16.mx-auto.mb-4 {
              width: 48px !important;
              height: 48px !important;
              margin-bottom: 12px !important;
            }

            /* Estilos para el control de volumen */
            input[type=range] {
              -webkit-appearance: none;
              appearance: none;
              background: transparent;
            }

            input[type=range]::-webkit-slider-thumb {
              -webkit-appearance: none;
              height: 15px;
              width: 15px;
              border-radius: 50%;
              background: var(--accent-color);
              cursor: pointer;
              margin-top: -6px;
            }

            input[type=range]::-moz-range-thumb {
              height: 15px;
              width: 15px;
              border-radius: 50%;
              background: var(--accent-color);
              cursor: pointer;
            }

            input[type=range]::-webkit-slider-runnable-track {
              width: 100%;
              height: 3px;
              background: var(--border-color);
              border-radius: 3px;
            }

            input[type=range]::-moz-range-track {
              width: 100%;
              height: 3px;
              background: var(--border-color);
              border-radius: 3px;
            }

            /* Control de volumen vertical */
            .volume-slider-vertical {
              transform: rotate(-90deg);
              width: 80px;
              margin: 10px 0;
            }
          }
        `}
      </style>
      <ThemeBackground />
      <div className="absolute inset-0 z-10 flex items-start justify-center overflow-y-auto">
        <div className="w-full max-w-7xl p-2 sm:p-6 pb-20 sm:pb-24 my-4 mobile-padding">
          <div className="themed-card backdrop-blur-md shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="p-3 sm:p-6 border-b" style={{ borderColor: 'var(--border-color)' }}>
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2 sm:space-x-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--button-bg)' }}>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: 'var(--accent-color)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold" style={{ color: 'var(--text-color)' }}>
                      {roomData?.name || `Sala: ${roomId}`}
                    </h2>
                    <div className="flex items-center space-x-2">
                      <span className="inline-block w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full animate-pulse"></span>
                      <span className="text-xs sm:text-sm" style={{ color: 'var(--text-secondary)' }}>En vivo</span>

                      {isOwner && (
                        <button
                          onClick={toggleRoomPublic}
                          className="ml-2 text-xs flex items-center rounded-full px-2 py-0.5 transition-colors"
                          style={{
                            backgroundColor: roomData?.is_public
                              ? 'rgba(34, 197, 94, 0.1)'
                              : 'rgba(239, 68, 68, 0.1)',
                            color: roomData?.is_public
                              ? 'rgb(34, 197, 94)'
                              : 'rgb(239, 68, 68)'
                          }}
                        >
                          <span className="mr-1">
                            {roomData?.is_public ? 'Pública' : 'Privada'}
                          </span>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={
                              roomData?.is_public
                                ? "M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                                : "M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            } />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {/* Botón avatar de usuario */}
                  <div
                    className="h-9 w-9 sm:h-10 sm:w-10 rounded-full overflow-hidden border cursor-pointer transition-all"
                    style={{ borderColor: 'var(--border-color)' }}
                    onClick={() => setIsProfileModalOpen(true)}
                  >
                    {profile?.avatar_url ? (
                      <img
                        src={profile?.avatar_url}
                        alt={profile?.username || 'Avatar'}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center" style={{ backgroundColor: 'var(--button-bg)' }}>
                        <svg className="w-5 h-5" style={{ color: 'var(--button-text)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    )}
                  </div>

                <button
                  onClick={handleLeaveRoom}
                  className="themed-button bg-red-500/20 text-red-400 px-3 py-1.5 sm:px-4 sm:py-2 flex items-center space-x-2"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span className="text-sm sm:text-base">Salir</span>
                </button>
                </div>
              </div>
            </div>

            {/* Contenido principal */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6 p-3 sm:p-6">
              {/* Reproductor de video */}
              <div className="lg:col-span-2 space-y-3 sm:space-y-6">
                <div className="relative overflow-hidden rounded-2xl shadow-lg w-full" style={{ aspectRatio: '16/9' }}>
                  {url ? (
                    <>
                      {isVideoLoading && !videoReady && (
                        <div
                          className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black bg-opacity-80"
                        >
                          <div className="p-6 text-center">
                            <div className="flex justify-center mb-4">
                              <div className="w-3 h-3 bg-blue-500 rounded-full mx-1 animate-dot-bounce delay-0"></div>
                              <div className="w-3 h-3 bg-blue-500 rounded-full mx-1 animate-dot-bounce delay-75"></div>
                              <div className="w-3 h-3 bg-blue-500 rounded-full mx-1 animate-dot-bounce delay-150"></div>
                            </div>
                            <h3 className="text-lg sm:text-xl font-medium mb-2 text-white">
                              Cargando video...
                            </h3>
                            <p className="text-sm text-gray-300">
                              El video comenzará automáticamente cuando esté listo
                            </p>
                            {loadedSeconds > 0 && (
                              <p className="text-xs text-gray-400 mt-2">
                                Cargados {loadedSeconds.toFixed(1)} segundos
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      <VideoPlayer
                        url={url}
                        playing={playing}
                        progress={progress}
                        onPlayPause={handlePlayPause}
                        onSeek={handleSeek}
                        onProgress={handleProgress}
                        onPlaybackRateChange={handlePlaybackRateChange}
                        playerRef={playerRef} // Pasar la referencia al componente VideoPlayer
                      />
                    </>
                  ) : (
                    <div
                      className="h-full w-full flex flex-col items-center justify-center"
                      style={{ backgroundColor: 'var(--card-bg)' }}
                    >
                      {isOwner ? (
                        <>
                          <div className="p-6 text-center">
                            <svg className="w-16 h-16 mx-auto mb-4 opacity-50 sm:opacity-20" style={{ color: 'var(--accent-color)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <h3 className="text-lg sm:text-xl font-medium mb-2" style={{ color: 'var(--text-color)' }}>
                              No hay ningún video en reproducción
                            </h3>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="p-6 text-center">
                            <svg className="w-16 h-16 mx-auto mb-4 opacity-10" style={{ color: 'var(--text-secondary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            <h3 className="text-lg sm:text-xl font-medium mb-2" style={{ color: 'var(--text-color)' }}>
                              Esperando video...
                            </h3>
                            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                              El anfitrión aún no ha seleccionado un video para reproducir
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Buscador de videos (solo visible para el propietario) */}
                {isOwner && <VideoSourceSelector onVideoSelect={handleVideoSelect} />}
              </div>

              {/* Chat */}
              <div className="themed-card overflow-hidden flex flex-col">
                <div className="p-3 sm:p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" style={{ color: 'var(--accent-color)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <h3 className="text-base sm:text-lg font-semibold" style={{ color: 'var(--text-color)' }}>
                        Chat de la sala
                      </h3>
                    </div>

                    {/* Controles de voz */}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={toggleMicrophone}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                          isMicActive
                            ? 'bg-green-500/30 text-green-400 hover:bg-green-500/20'
                            : 'themed-button'
                        }`}
                        title={isMicActive ? "Desactivar micrófono" : "Activar micrófono"}
                      >
                        {isMicActive ? (
                          <svg className="w-4 h-4" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                            <line x1="12" y1="19" x2="12" y2="23" />
                            <line x1="8" y1="23" x2="16" y2="23" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="1" y1="1" x2="23" y2="23" />
                            <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                            <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
                            <line x1="12" y1="19" x2="12" y2="23" />
                            <line x1="8" y1="23" x2="16" y2="23" />
                          </svg>
                        )}
                      </button>

                      <div
                        className="relative"
                        ref={volumeControlRef}
                        onMouseEnter={() => setShowVolumeSlider(true)}
                      >
                        <button
                          onClick={toggleSpeaker}
                          className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                            isSpeakerActive
                              ? 'bg-blue-500/30 text-blue-400 hover:bg-blue-500/20'
                              : 'themed-button'
                          }`}
                          title={isSpeakerActive ? "Silenciar altavoz" : "Activar altavoz"}
                        >
                          {isSpeakerActive ? (
                            volume > 0.6 ? (
                              <svg className="w-4 h-4" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                              </svg>
                            ) : volume > 0.2 ? (
                              <svg className="w-4 h-4" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                              </svg>
                            )
                          ) : (
                            <svg className="w-4 h-4" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                              <line x1="23" y1="9" x2="17" y2="15" />
                              <line x1="17" y1="9" x2="23" y2="15" />
                            </svg>
                          )}
                        </button>

                        {showVolumeSlider && (
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 p-3 rounded-lg shadow-lg z-50"
                               style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)', border: '1px solid rgba(59, 130, 246, 0.2)', width: '80px' }}>
                            <div className="flex flex-col items-center">
                              <div className="h-20 flex flex-col items-center justify-center relative">
                                <div className="w-1 h-full bg-gray-700 rounded-full absolute">
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.01"
                                  value={volume}
                                  onChange={handleVolumeChange}
                                  className="volume-slider-vertical appearance-none bg-transparent z-10"
                                  style={{
                                    width: '60px',
                                    height: '20px',
                                    transform: 'rotate(-90deg) translateX(-20px)',
                                    transformOrigin: 'left center'
                                  }}
                                />
                                <div
                                  className="w-1 bg-blue-500 rounded-full absolute bottom-0 left-0 right-0 mx-auto pointer-events-none"
                                  style={{
                                    height: `${volume * 100}%`
                                  }}
                                ></div>
                              </div>
                              <span className="text-xs mt-2 text-white">
                                {Math.round(volume * 100)}%
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contenedor de Mensajes e Input (que ahora tomará la altura disponible) */}
                <div className="flex flex-col flex-1 overflow-hidden h-full max-h-[calc(100vh-280px)]"> {/* Añadir flex-1, overflow-hidden, h-full y max-h */}
                  {/* Mensajes */}
                  <div
                    className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 chat-messages h-full"
                    onScroll={handleChatScroll}
                    ref={messagesEndRef}
                  >
                    {messages.map((message, index) => {
                      // Corregir la comparación para usar user.id en lugar de user.email
                      const isCurrentUser = message.user === user?.id;
                      const isSystemMessage = message.user === 'system';
                      const messageId = message.id || message.timestamp.toString();
                      const isRead = readMessageIds[messageId] || false; // Esto es para el lector local, no para el indicador del emisor

                      // Eliminar el log excesivo
                      // if (isCurrentUser) {
                      //  console.log(`[RenderCheck] Mensaje propio (ID: ${messageId}): readBy=`, message.readBy);
                      // }

                      return (
                        <div
                          key={index}
                          className={`flex animate-fade-slide-in ${isCurrentUser ? 'justify-end' : ''}`}
                          style={{
                            animationDuration: '0.3s',
                            opacity: 0
                          }}
                        >
                          {/* Avatar para otros usuarios o sistema */}
                          {!isCurrentUser && (
                            <div className="flex-shrink-0 mt-1">
                              {isSystemMessage ? (
                                <div className="h-9 w-9 rounded-full bg-yellow-500/20 flex items-center justify-center">
                                  <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                  </svg>
                                </div>
                              ) : message.avatar_url ? (
                                <div className="h-9 w-9 rounded-full overflow-hidden bg-gray-800/70 border border-gray-700/50">
                                  <img
                                    src={message.avatar_url}
                                    alt={message.username || 'Avatar'}
                                    className="h-full w-full object-cover"
                                  />
                                </div>
                              ) : (
                                <div className="h-9 w-9 rounded-full bg-blue-500/20 flex items-center justify-center">
                                  <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                </div>
                              )}
                            </div>
                          )}

                          <div className={`${isCurrentUser ? 'items-end' : 'ml-3'} flex-1 flex flex-col ${isCurrentUser ? 'items-end' : 'items-start'}`}>
                            <div className="flex items-center">
                              <span className={`text-xs sm:text-sm font-medium ${isSystemMessage ? 'text-yellow-400' : 'text-blue-400'}`}>
                                {message.username || message.user}
                              </span>
                            </div>
                            <div
                              className={`${
                                isCurrentUser
                                  ? 'bg-blue-600/50'
                                  : isSystemMessage
                                    ? 'bg-yellow-500/10 border border-yellow-500/20'
                                    : 'bg-gray-800/50'
                              } rounded-lg px-3 py-1.5 sm:px-4 sm:py-2 text-sm sm:text-base text-white mt-1 inline-block max-w-[80%] chat-message-text`}
                            >
                              {message.text}
                            </div>
                            <div className="text-xs text-gray-500 mt-1 flex items-center">
                              <span>{formatTime(message.timestamp)}</span>

                              {/* Indicador de lectura: Mostrar en MIS mensajes si ALGUIEN MÁS lo leyó */}
                              {isCurrentUser &&
                               message.readBy &&
                               message.readBy.some(readerId => readerId !== user?.id) && (
                                <div
                                  className="ml-2 flex items-center animate-bounce-in"
                                  title={`Visto por ${message.readBy.filter(id => id !== user?.id).length} persona(s)`}
                                >
                                  {/* Usamos un icono de doble check verde */}
                                  <svg className="h-4 w-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" transform="translate(-4, 0)"/>
                                  </svg>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Avatar para el usuario actual a la derecha */}
                          {isCurrentUser && (
                            <div className="flex-shrink-0 mt-1 ml-3">
                              {message.avatar_url ? (
                                <div className="h-9 w-9 rounded-full overflow-hidden bg-gray-800/70 border border-gray-700/50">
                                  <img
                                    src={message.avatar_url}
                                    alt={message.username || 'Avatar'}
                                    className="h-full w-full object-cover"
                                  />
                                </div>
                              ) : (
                                <div className="h-9 w-9 rounded-full bg-blue-500/20 flex items-center justify-center">
                                  <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {usersTyping.length > 0 && (
                      <div className="flex items-center space-x-2 text-gray-400 text-xs">
                        <div className="flex space-x-1">
                          <span className="animate-dot-bounce delay-0">•</span>
                          <span className="animate-dot-bounce delay-75">•</span>
                          <span className="animate-dot-bounce delay-150">•</span>
                        </div>
                        <span>
                          {usersTyping.length === 1
                            ? `${usersTyping[0]} está escribiendo...`
                            : `${usersTyping.length} personas están escribiendo...`}
                        </span>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Indicador de mensajes no leídos */}
                  {unreadMessages > 0 && (
                    <div
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-medium px-3 py-1.5 rounded-full absolute bottom-24 right-6 cursor-pointer shadow-xl flex items-center gap-1.5 hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 border border-blue-400/20"
                      onClick={() => {
                        if (messagesEndRef.current && messagesEndRef.current.parentElement) {
                          messagesEndRef.current.parentElement.scrollTop = messagesEndRef.current.parentElement.scrollHeight;
                          setUnreadMessages(0);
                        }
                      }}
                      style={{
                        animation: 'bounce 1s infinite alternate',
                      }}
                    >
                      <span className="font-bold">{unreadMessages}</span>
                      <span className="text-blue-200">nuevos</span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                      </svg>
                    </div>
                  )}

                  {/* Input del chat */}
                  <div className="mt-auto border-t p-3 sm:p-4 bg-opacity-95 backdrop-blur-sm" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--card-bg)' }}>
                    <form onSubmit={handleSendMessage} className="relative group chat-form">
                      <div className="absolute -inset-0.5 rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-300" style={{ background: `linear-gradient(to right, var(--accent-color), rgba(147, 51, 234, 0.3))` }}></div>
                      <div className="relative flex items-center">
                        <input
                          type="text"
                          value={inputValue} // Usar inputValue en lugar de newMessage
                          onChange={handleInputChange}
                          placeholder="Escribe un mensaje..."
                          className="themed-input flex-1 rounded-l-xl px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base focus:ring-2 transition-all chat-input"
                          style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 0 }}
                          onFocus={handleInputFocus}
                        />
                        <button
                          type="submit"
                          className="themed-button rounded-r-xl border px-4 py-2 sm:px-5 sm:py-3 transition-all duration-200 hover:scale-105 flex items-center justify-center chat-submit"
                          style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                        >
                          <svg className="w-5 h-5 sm:mr-1" style={{ color: 'var(--button-text)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                          </svg>
                          <span className="hidden sm:inline">Enviar</span>
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de perfil de usuario */}
      <UserProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />

      <ScrollToTop />
    </div>
  );
};

export default Room;